import {
  ImportHeaderStatusName,
  ImportDetailStatusName,
  ImportStatusId,
} from '../interfaces/Import/ShipmentBulkUploadApi'

/**
 * Mapeo de estados de carga masiva a etiquetas (es) y clases de badge (Tailwind),
 * más mapeo de códigos de error a mensajes UX y clasificación transitorio/dato.
 *
 * Calibrado a DESIGN.md (tokens semánticos):
 *   InProcess → main (azul env-aware) · Finalized → success #16a34a
 *   FinalizedWithErrors → warning #f59e0b · Cancelled → gray · Deleted → error #dc2626
 *   Executed (detalle) → success · Error → error · Pending → gray
 *
 * Resolución por `status_name` (estable); el `status_id` numérico (68–75) es solo fallback,
 * porque los IDs son placeholders del backend y pueden colisionar con [config].[Status].
 */

export type StatusTone = 'info' | 'success' | 'warning' | 'error' | 'neutral'

export interface StatusMeta {
  label: string
  /** Clases Tailwind para el badge (pill). */
  badgeClass: string
  tone: StatusTone
}

const HEADER_META: Record<string, StatusMeta> = {
  [ImportHeaderStatusName.InProcess]: {
    label: 'En proceso',
    badgeClass: 'bg-main-50 text-main-600',
    tone: 'info',
  },
  [ImportHeaderStatusName.Finalized]: {
    label: 'Finalizado',
    badgeClass: 'bg-green-50 text-green-700',
    tone: 'success',
  },
  [ImportHeaderStatusName.FinalizedWithErrors]: {
    label: 'Finalizado con errores',
    badgeClass: 'bg-amber-50 text-amber-700',
    tone: 'warning',
  },
  [ImportHeaderStatusName.Cancelled]: {
    label: 'Cancelado',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportHeaderStatusName.Deleted]: {
    label: 'Eliminado',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
}

const DETAIL_META: Record<string, StatusMeta> = {
  [ImportDetailStatusName.Pending]: {
    label: 'Pendiente',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportDetailStatusName.Executing]: {
    label: 'En ejecución',
    badgeClass: 'bg-main-50 text-main-600',
    tone: 'info',
  },
  [ImportDetailStatusName.Executed]: {
    label: 'OK',
    badgeClass: 'bg-green-50 text-green-700',
    tone: 'success',
  },
  [ImportDetailStatusName.Error]: {
    label: 'Error',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
  [ImportDetailStatusName.Substituted]: {
    label: 'Sustituida',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportDetailStatusName.ErrorProcessing]: {
    label: 'Error procesando',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
  [ImportDetailStatusName.ErrorServer]: {
    label: 'Error servidor',
    badgeClass: 'bg-amber-50 text-amber-700',
    tone: 'warning',
  },
}

/** Fallback por ID numérico → nombre interno (si el backend no manda status_name). */
const HEADER_ID_TO_NAME: Record<number, string> = {
  [ImportStatusId.HeaderInProcess]: ImportHeaderStatusName.InProcess,
  [ImportStatusId.HeaderFinalized]: ImportHeaderStatusName.Finalized,
  [ImportStatusId.HeaderFinalizedWithErrors]:
    ImportHeaderStatusName.FinalizedWithErrors,
  [ImportStatusId.HeaderCancelled]: ImportHeaderStatusName.Cancelled,
  [ImportStatusId.HeaderDeleted]: ImportHeaderStatusName.Deleted,
}

const DETAIL_ID_TO_NAME: Record<number, string> = {
  [ImportStatusId.DetailPending]: ImportDetailStatusName.Pending,
  [ImportStatusId.DetailExecuting]: ImportDetailStatusName.Executing,
  [ImportStatusId.DetailExecuted]: ImportDetailStatusName.Executed,
  [ImportStatusId.DetailError]: ImportDetailStatusName.Error,
  [ImportStatusId.DetailSubstituted]: ImportDetailStatusName.Substituted,
  [ImportStatusId.DetailErrorProcessing]: ImportDetailStatusName.ErrorProcessing,
  [ImportStatusId.DetailErrorServer]: ImportDetailStatusName.ErrorServer,
}

const UNKNOWN_META: StatusMeta = {
  label: 'Desconocido',
  badgeClass: 'bg-gray-100 text-gray-600',
  tone: 'neutral',
}

export function headerStatusMeta(
  statusName?: string | null,
  statusId?: number | null
): StatusMeta {
  if (statusName && HEADER_META[statusName]) return HEADER_META[statusName]
  if (statusId != null && HEADER_ID_TO_NAME[statusId]) {
    return HEADER_META[HEADER_ID_TO_NAME[statusId]]
  }
  return UNKNOWN_META
}

export function detailStatusMeta(
  statusName?: string | null,
  statusId?: number | null
): StatusMeta {
  if (statusName && DETAIL_META[statusName]) return DETAIL_META[statusName]
  if (statusId != null && DETAIL_ID_TO_NAME[statusId]) {
    return DETAIL_META[DETAIL_ID_TO_NAME[statusId]]
  }
  return UNKNOWN_META
}

/**
 * Resuelve el nombre canónico del estado de detalle con el mismo fallback name→id
 * que usa `detailStatusMeta`. Necesario para decidir acciones (ver guía / ver payload /
 * eliminar) y contar ejecutados/errores: comparar el `status_name` crudo contra el enum
 * falla cuando el backend manda un name distinto y solo coincide por `status_id`.
 */
export function resolveDetailStatusName(
  statusName?: string | null,
  statusId?: number | null
): ImportDetailStatusName | null {
  if (statusName && DETAIL_META[statusName]) {
    return statusName as ImportDetailStatusName
  }
  if (statusId != null && DETAIL_ID_TO_NAME[statusId]) {
    return DETAIL_ID_TO_NAME[statusId] as ImportDetailStatusName
  }
  return null
}

// ---------------------------------------------------------------------------
// Reglas de acciones de carga masiva (alineadas al backend)
//
// Los botones se calculan agregando los `status_id` de los detalles (la spec exige
// que el front no habilite lo que el backend rechazaría). Estatus de detalle:
//   73 Pendiente · 74 Ejecutando/OK · 75 Error · 76 Sustituida · 77 Error procesando ·
//   78 Error servidor · 79 En ejecución (procesándose ahora)
// ---------------------------------------------------------------------------

/** Tope de reintentos del backend para errores de servidor (78); se muestra "intento N/40". */
export const SERVER_ERROR_RETRY_CAP = 40

/**
 * Un detalle se puede eliminar salvo cuando ya generó el envío real (74 Ejecutando/OK)
 * o está creándolo justo ahora (79 En ejecución): en ambos casos borrarlo dejaría estado
 * inconsistente contra el backend.
 */
export function canDeleteDetailStatus(
  statusName?: string | null,
  statusId?: number | null
): boolean {
  const s = resolveDetailStatusName(statusName, statusId)
  return (
    s !== ImportDetailStatusName.Executed &&
    s !== ImportDetailStatusName.Executing
  )
}

export interface BulkActionFlags {
  pendingCount: number
  /** Detalles procesándose ahora mismo (79). */
  runningCount: number
  /** Detalles que ya generaron el envío real (74). */
  executedCount: number
  substitutedCount: number
  /** Errores de dato que el usuario corrige (75 + 77). */
  dataErrorCount: number
  /** Errores de servidor reintentables (78). */
  serverErrorCount: number
  hasExecuting: boolean
  /** Hay 75/77 → habilita "Corregir errores". */
  canEditErrors: boolean
  /** Hay 78 y NINGÚN 75/77 → habilita "Reintentar". Mutuamente excluyente con canEditErrors. */
  canRetry: boolean
  /** Cabecera 70 (siempre) o 68 sin ningún 74. */
  canDeleteHeader: boolean
}

/**
 * Deriva el estado de los botones de la carga masiva desde los estatus de sus detalles
 * más el estatus de la cabecera. Puro y testeable (las tablas de verdad de la spec son
 * los casos de prueba). Resuelve cada detalle con el mismo fallback name→id del resto.
 */
export function getBulkActionFlags(
  details: Array<{ status_name?: string | null; status_id?: number | null }>,
  headerStatusName?: string | null,
  headerStatusId?: number | null
): BulkActionFlags {
  let pendingCount = 0
  let runningCount = 0
  let executedCount = 0
  let substitutedCount = 0
  let dataErrorCount = 0
  let serverErrorCount = 0

  for (const d of details) {
    switch (resolveDetailStatusName(d.status_name, d.status_id)) {
      case ImportDetailStatusName.Pending:
        pendingCount++
        break
      case ImportDetailStatusName.Executing:
        runningCount++
        break
      case ImportDetailStatusName.Executed:
        executedCount++
        break
      case ImportDetailStatusName.Substituted:
        substitutedCount++
        break
      case ImportDetailStatusName.Error:
      case ImportDetailStatusName.ErrorProcessing:
        dataErrorCount++
        break
      case ImportDetailStatusName.ErrorServer:
        serverErrorCount++
        break
      default:
        break
    }
  }

  const hasExecuting = executedCount > 0

  // Estatus de cabecera con el mismo fallback name→id que headerStatusMeta.
  const headerName =
    (headerStatusName && HEADER_META[headerStatusName]
      ? headerStatusName
      : headerStatusId != null
      ? HEADER_ID_TO_NAME[headerStatusId]
      : null) ?? null
  const isInProcess = headerName === ImportHeaderStatusName.InProcess
  const isFinalizedWithErrors =
    headerName === ImportHeaderStatusName.FinalizedWithErrors

  return {
    pendingCount,
    runningCount,
    executedCount,
    substitutedCount,
    dataErrorCount,
    serverErrorCount,
    hasExecuting,
    canEditErrors: dataErrorCount > 0,
    canRetry: serverErrorCount > 0 && dataErrorCount === 0,
    canDeleteHeader:
      isFinalizedWithErrors ||
      (isInProcess && !hasExecuting && runningCount === 0),
  }
}

// ---------------------------------------------------------------------------
// Mapeo de códigos de error → mensajes UX (Frontend Spec sección 7)
// ---------------------------------------------------------------------------

export const IMPORT_ERROR_MESSAGES: Record<string, string> = {
  'shipping.ShipmentBulkUploadHeader.1': 'La carga masiva no existe o fue eliminada',
  'shipping.ShipmentBulkUploadHeader.2':
    'Esta carga masiva ya fue finalizada o cancelada, no se puede modificar',
  'shipping.ShipmentBulkUploadHeader.3': 'Ya existe una carga masiva con ese número',
  'shipping.ShipmentBulkUploadHeader.4': 'La carga masiva debe tener al menos una guía',
  'shipping.ShipmentBulkUploadHeader.5': 'No se pudo guardar el archivo en la nube',
  'shipping.ShipmentBulkUploadHeader.6': 'Esta carga masiva no tiene archivo asociado',
  'shipping.ShipmentBulkUploadHeader.7':
    'Debes subir un archivo o indicar el motivo de la carga masiva',
  'shipping.ShipmentBulkUploadHeader.8': 'El archivo supera los 25 MB permitidos',
  'shipping.ShipmentBulkUploadHeader.9':
    'El sistema de procesamiento no está disponible. Reintentá en unos minutos.',
  'shipping.ShipmentBulkUploadHeader.10':
    'Tipo de archivo no soportado. Subí CSV, Excel o JSON.',
  'shipping.ShipmentBulkUploadHeader.11':
    'No tenés permisos para acceder a esta carga masiva',
  'shipping.ShipmentBulkUploadHeader.12':
    'La carga masiva debe referenciar un sitio de cuenta o una cuenta',
  'shipping.ShipmentBulkUploadDetail.1': 'El detalle no existe',
  'shipping.ShipmentBulkUploadDetail.2':
    'Esta guía ya fue procesada y no se puede modificar',
  'general.General.5': 'Hay datos inválidos. Revisá los campos marcados.',
}

/** Mensaje UX para un code del envelope; cae al fallback si no está mapeado. */
export function messageForCode(
  code?: string | null,
  fallback = 'Ocurrió un error. Intentá de nuevo.'
): string {
  if (code && IMPORT_ERROR_MESSAGES[code]) return IMPORT_ERROR_MESSAGES[code]
  return fallback
}

// ---------------------------------------------------------------------------
// error_data → errores por campo/fila (general.General.5: validación pydantic)
//
// El backend devuelve claves tipo "shipment_list.0.consignee.phone". Las traducimos
// al índice de grupo + la key de ShipmentGroupErrors para pintar el campo exacto en
// el Review en lugar de un modal genérico. Mapeo conservador: lo que no reconocemos
// se devuelve en `unmapped` para mostrarlo agregado (no se pierde información).
// ---------------------------------------------------------------------------

/** path del backend (luego de `shipment_list.<i>.`) → key de ShipmentGroupErrors. */
const SERVER_FIELD_TO_GROUP_KEY: Record<string, string> = {
  'consignee.phone': 'consigneePhone',
  'consignee.identification': 'consigneeDocNumber',
  'consignee.name': 'consigneeName',
  'consignee.email': 'consigneeEmail',
  'destination.address.line': 'destinationAddress',
  'destination.location': 'destinationCity',
  'destination.store': 'storeCode',
  'partner_shipment_id': 'trackingNumber',
  'shipper.name': 'shipperName',
  'shipper.identification': 'shipperDocNumber',
  // shipper.phone NO se mapea a propósito: viene de user.client y no es editable
  // por fila, así que cae en `unmapped` para evitar un error fantasma que bloquee
  // el envío sin un campo donde corregirlo.
}

/** Traduce el mensaje crudo del backend a algo legible en español. */
function translateServerFieldMessage(path: string, rawMsg: string): string {
  const cleaned = (rawMsg || '').replace(/^Value error,\s*/i, '').trim()
  if (path.endsWith('.phone')) {
    const phone = /(\+\d{1,3} \d+)/.exec(rawMsg)?.[1] || ''
    return phone
      ? `Teléfono inválido (${phone}). Debe tener entre 9 y 15 dígitos.`
      : 'Teléfono inválido. Debe tener entre 9 y 15 dígitos.'
  }
  return cleaned || 'Dato inválido'
}

export interface ServerFieldErrors {
  /** índice de grupo → { keyDeShipmentGroupErrors → mensaje } */
  byIndex: Record<number, Record<string, string>>
  /** mensajes que no se pudieron asociar a un campo editable */
  unmapped: string[]
}

/**
 * Convierte el `error_data` del envelope en errores por fila/campo para el Review.
 * Acepta claves "shipment_list.<i>.<path>"; cualquier otra forma cae en `unmapped`.
 */
export function mapServerFieldErrors(
  errorData?: Record<string, any> | null
): ServerFieldErrors {
  const byIndex: Record<number, Record<string, string>> = {}
  const unmapped: string[] = []
  if (!errorData || typeof errorData !== 'object') return { byIndex, unmapped }

  for (const [rawKey, rawVal] of Object.entries(errorData)) {
    const rawMsg = typeof rawVal === 'string' ? rawVal : JSON.stringify(rawVal)
    const m = /^shipment_list\.(\d+)\.(.+)$/.exec(rawKey)
    if (!m) {
      unmapped.push(rawMsg)
      continue
    }
    const idx = parseInt(m[1], 10)
    const path = m[2]
    const groupKey = SERVER_FIELD_TO_GROUP_KEY[path]
    const message = translateServerFieldMessage(path, rawMsg)
    if (!groupKey) {
      unmapped.push(message)
      continue
    }
    byIndex[idx] = { ...(byIndex[idx] || {}), [groupKey]: message }
  }

  return { byIndex, unmapped }
}

// ---------------------------------------------------------------------------
// Clasificación de errores: transitorio (auto-retry) vs dato (humano)
// ---------------------------------------------------------------------------

export type ImportErrorKind = 'transient' | 'data'

/**
 * Códigos claramente transitorios — se reintentan solos (con cap + backoff aguas arriba).
 * Conservador a propósito: un error desconocido NO es transitorio (se trata como 'data'
 * para evitar loops de reintento contra un backend que falla por una causa real).
 *
 * Nota (dependencia backend abierta): los errores por detalle llegan hoy como string
 * (`error_message`), no como code estructurado. Hasta que el backend exponga un code/categoría
 * por detalle, la clasificación de detalles es match de string y debe quedar conservadora.
 */
const TRANSIENT_CODES = new Set<string>([
  'shipping.ShipmentBulkUploadHeader.9', // cola Redis / procesamiento no disponible
  'shipping.ShipmentBulkUploadHeader.5', // fallo al subir archivo a Spaces
])

const TRANSIENT_MESSAGE_PATTERNS: RegExp[] = [
  /\btimeout\b/i,
  /\btemporarily unavailable\b/i,
  /\bservice unavailable\b/i,
  /\bconnection (refused|reset|error)\b/i,
  /\b50[0234]\b/, // 500/502/503/504
  /\bredis\b/i,
  /\bqueue\b/i,
]

export function classifyImportError(
  code?: string | null,
  message?: string | null
): ImportErrorKind {
  if (code && TRANSIENT_CODES.has(code)) return 'transient'
  if (message && TRANSIENT_MESSAGE_PATTERNS.some((re) => re.test(message))) {
    return 'transient'
  }
  return 'data'
}
